import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState, useMemo, useEffect } from "react";
import { Tabs } from "@/Tabs";
import { useTab } from "^/common/useTab";
import { LayoutHeader } from "@/Header";
import { Button } from "@/Button";
import { Dialog } from "@/Dialog";
import { SubscriptionPlan, ServiceType } from "@/business/subscription/types";
import { SubscriptionPlanCard } from "@/business/subscription/SubscriptionPlanCard";
import {
  useBusinessMypageTicketList,
  useDupleReserveSetting,
  useUpdateDupleReserveSetting,
} from "~/services/business/myPage";
import { useBusinessAuthUserInfo } from "~/services/business/auth";
import ToggleSwitch from "@/common/ToggleSwitch";

export const Route = createFileRoute(
  "/business/layout/user-subscription-management"
)({
  component: SubscriptionManagement,
});

function SubscriptionManagement() {
  const navigate = useNavigate();
  const { tab, handleTab, tabList } = useTab(["유치원", "호텔", "데이케어"]);
  const [subscriptionPlans, setSubscriptionPlans] = useState<
    SubscriptionPlan[]
  >([]);
  const [isValidityAlertOpen, setIsValidityAlertOpen] = useState(false);

  // API에서 실제 이용권 목록 가져오기
  const { data: ticketListData } = useBusinessMypageTicketList();

  // 권한 정보 (원장만 중복예약 허용 토글 노출)
  const { data: authUserInfo } = useBusinessAuthUserInfo();
  const isOwner = authUserInfo?.isOwner ?? false;

  // 중복예약 허용 설정 (원장일 때만 조회)
  const { data: dupleSetting } = useDupleReserveSetting(isOwner);
  const { mutate: updateDupleSetting } = useUpdateDupleReserveSetting();
  const [duplicateAllowed, setDuplicateAllowed] = useState(false);

  // 서버 값으로 토글 상태 동기화
  useEffect(() => {
    if (dupleSetting?.duplicateReservYn) {
      setDuplicateAllowed(dupleSetting.duplicateReservYn === "Y");
    }
  }, [dupleSetting?.duplicateReservYn]);

  // 토글 변경 시 즉시 PATCH (실패하면 원복)
  const handleDuplicateToggle = (checked: boolean) => {
    setDuplicateAllowed(checked);
    updateDupleSetting(
      { duplicateReservYn: checked ? "Y" : "N" },
      {
        onError: () => {
          setDuplicateAllowed(!checked);
        },
      }
    );
  };

  // API 응답 타입 정의
  interface TicketItem {
    productTicketSeq: number;
    companySeq: number;
    cdTicketType: string;
    cdProductType: string;
    name: string;
    contents: string;
    cost: number;
    discount: number;
    amount: number;
    splitYn: string;
    splitCount: number;
    badge: string[];
    activeYn: string;
    dataOrder: number;
    durationDays?: number | null;
    dtExpired?: string | null;
  }

  // API 응답을 SubscriptionPlan 형식으로 변환
  const convertTicketToPlan = (
    ticket: TicketItem,
    serviceType: ServiceType
  ): SubscriptionPlan => {
    // badge는 이미 배열로 받음
    const badges = ticket.badge || [];

    return {
      planSeq: ticket.productTicketSeq,
      planName: ticket.name,
      content: ticket.contents || "",
      price: ticket.cost,
      discountRate: ticket.discount || 0,
      discountedPrice: ticket.amount,
      badges: badges,
      activeYn: ticket.activeYn === "Y" ? "Y" : "N",
      displayOrder: ticket.dataOrder,
      serviceType: serviceType,
      isCountBased: ticket.splitYn === "Y",
      count: ticket.splitCount || undefined,
      durationDays: ticket.durationDays ?? null,
      dtExpired: ticket.dtExpired ?? null,
    };
  };

  // API 데이터를 SubscriptionPlan 배열로 변환
  const apiPlans = useMemo(() => {
    if (!ticketListData) return [];

    const plans: SubscriptionPlan[] = [];

    // 유치원
    if (ticketListData.kindergarten) {
      plans.push(
        ...ticketListData.kindergarten.map((ticket: TicketItem) =>
          convertTicketToPlan(ticket, "유치원")
        )
      );
    }

    // 호텔
    if (ticketListData.hotel) {
      plans.push(
        ...ticketListData.hotel.map((ticket: TicketItem) =>
          convertTicketToPlan(ticket, "호텔")
        )
      );
    }

    // 데이케어
    if (ticketListData.daycare) {
      plans.push(
        ...ticketListData.daycare.map((ticket: TicketItem) =>
          convertTicketToPlan(ticket, "데이케어")
        )
      );
    }

    // 미용실
    if (ticketListData.grooming) {
      plans.push(
        ...ticketListData.grooming.map((ticket: TicketItem) =>
          convertTicketToPlan(ticket, "미용실")
        )
      );
    }

    return plans;
  }, [ticketListData]);

  // API 데이터가 있으면 사용, 없으면 빈 배열
  useEffect(() => {
    if (apiPlans.length > 0) {
      setSubscriptionPlans(apiPlans);
    }
  }, [apiPlans]);

  // API 데이터 사용
  const currentPlansRaw = subscriptionPlans;

  // 선택된 탭에 해당하는 이용권만 필터링
  const filteredPlans = currentPlansRaw.filter(
    (plan) => plan.serviceType === tab || !plan.serviceType
  );

  // displayOrder에 따라 정렬 (없으면 기존 순서 유지)
  const currentPlans = [...filteredPlans].sort((a, b) => {
    const orderA = a.displayOrder ?? a.planSeq ?? 0;
    const orderB = b.displayOrder ?? b.planSeq ?? 0;
    return orderA - orderB;
  });

  // 추가 화면으로 이동
  const handleAddClick = () => {
    navigate({
      to: "/business/layout/user-subscription-add-edit",
      search: {
        productTicketSeq: undefined,
        serviceType: tab as ServiceType,
      },
    });
  };

  // 수정 화면으로 이동
  const handleEditClick = (plan: SubscriptionPlan) => {
    if (plan.planSeq) {
      navigate({
        to: "/business/layout/user-subscription-add-edit",
        search: {
          productTicketSeq: plan.planSeq,
          serviceType: (plan.serviceType || tab) as ServiceType,
        },
      });
    }
  };

  // 이용권 발급 화면으로 이동
  const handleIssueClick = (plan: SubscriptionPlan) => {
    const hasFixedExpiredDate = plan.durationDays == null && !!plan.dtExpired;
    const isExpiredOrToday =
      hasFixedExpiredDate && new Date(plan.dtExpired as string).getTime() <= Date.now();

    if (isExpiredOrToday) {
      setIsValidityAlertOpen(true);
      return;
    }

    if (plan.planSeq) {
      navigate({
        to: "/business/layout/user-subscription-issue",
        search: {
          productTicketSeq: plan.planSeq,
          serviceType: (plan.serviceType || tab) as ServiceType,
        },
      });
    }
  };

  const handleOrderEditClick = () => {
    navigate({ to: "/business/layout/user-subscription-order-edit" });
  };

  return (
    <>
      <LayoutHeader title={"이용권 관리"} hasBackBtn hasFootPrint={false} />
      <div className="flex-1 min-h-0 overflow-y-auto flex flex-col bg-[#F5F5F5]">
        {/* 탭 */}
        <Tabs tabList={tabList} handleTab={handleTab} tab={tab}>
          <div className="flex-auto gap-4 px-3 pt-2">
            {/* 중복예약 허용 토글(원장만) + 편집 버튼 */}
            <div className="flex justify-end items-center gap-3 pb-2 border-b border-gray-200">
              {isOwner && (
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-700">
                    중복예약 허용
                  </span>
                  <ToggleSwitch
                    checked={duplicateAllowed}
                    onChange={handleDuplicateToggle}
                  />
                </div>
              )}
              <button
                onClick={handleOrderEditClick}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white rounded-lg border border-gray-300 transition-colors hover:bg-gray-50"
              >
                편집
              </button>
            </div>
            {/* 이용권 목록 */}
            {currentPlans.length === 0 ? (
              <div className="flex flex-col justify-center items-center py-20">
                <p className="mb-4 text-gray-500">등록된 이용권이 없습니다.</p>
              </div>
            ) : (
              <div className="flex flex-col gap-3">
                {currentPlans.map((plan) => (
                  <SubscriptionPlanCard
                    key={plan.planSeq}
                    plan={plan}
                    onEdit={handleEditClick}
                    onIssue={handleIssueClick}
                  />
                ))}
              </div>
            )}
            {/* 이용권 추가하기 버튼 */}
            <div className="pt-4 pb-6">
              <Button
                onClick={handleAddClick}
                className="w-full py-3 text-base font-semibold text-white bg-orange-400 rounded-lg"
              >
                이용권 추가하기
              </Button>
            </div>
          </div>
        </Tabs>
      </div>

      <Dialog
        visible={isValidityAlertOpen}
        onClose={() => setIsValidityAlertOpen(false)}
        persist={false}
      >
        <div className="flex flex-col gap-1">
          <div className="flex flex-col mb-2">
            <div className="text-lg font-semibold text-center">알림</div>
          </div>
          <div className="flex flex-col mb-2">
            <div className="text-sm text-center text-[#666666]">
              유효기간 변경이 필요합니다.
            </div>
          </div>
          <div className="flex gap-3 mt-2">
            <button
              className="flex-1 py-2 font-semibold text-white bg-orange-400 rounded-full"
              onClick={() => setIsValidityAlertOpen(false)}
            >
              확인
            </button>
          </div>
        </div>
      </Dialog>


    </>
  );
}
